/** Phase 4.3-β — Chain instance detail with vertical stepper + decision UX.
 *
 * Renders every step of the chain definition + its run state, surfaces the
 * approve/reject affordance to the eligible logged-in user, and supports
 * cancelling a pending instance (owner / requester). See ADR-0029.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AuditHistoryLink from "../../components/app/AuditHistoryLink";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  CircleCheck, CircleX, Clock, MinusCircle, ChevronLeft,
} from "lucide-react";

const STATUS_VARIANTS = {
  pending: "secondary", approved: "default", rejected: "destructive",
  cancelled: "outline", expired: "outline",
};

function StepIcon({ status }) {
  const cls = "size-5 shrink-0";
  if (status === "approved") return <CircleCheck className={`${cls} text-emerald-600`} />;
  if (status === "rejected") return <CircleX className={`${cls} text-red-600`} />;
  if (status === "skipped") return <MinusCircle className={`${cls} text-muted-foreground`} />;
  if (status === "expired") return <Clock className={`${cls} text-amber-600`} />;
  return <Clock className={`${cls} text-blue-600`} />;
}

function StatusBadge({ status }) {
  return (
    <Badge data-testid={`chain-detail-status-${status}`} variant={STATUS_VARIANTS[status] || "outline"}>
      {status}
    </Badge>
  );
}

export default function ApprovalChainDetail() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const me = auth.getUser() || {};

  const [inst, setInst] = useState(null);
  const [defn, setDefn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/approval-chains/instances/${id}`);
      setInst(r.data);
      if (r.data?.chain_definition_id) {
        const dr = await api.get(
          `/approval-chains/definitions/${r.data.chain_definition_id}`
        );
        setDefn(dr.data);
      }
    } catch (e) {
      toast.error(t("chains.load_error"));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const merged = useMemo(() => {
    if (!defn) return [];
    const runById = {};
    for (const r of inst?.step_runs || []) runById[r.step_id] = r;
    return (defn.steps || []).map((s, i) => ({
      step_id: s.step_id, name: s.name || s.step_id,
      decision_policy: s.decision_policy, approvers: s.approvers,
      condition: s.condition, on_reject: s.on_reject, on_timeout: s.on_timeout,
      quorum_count: s.quorum_count, idx: i, run: runById[s.step_id],
    }));
  }, [defn, inst]);

  const currentStep = useMemo(() => {
    if (!inst || inst.status !== "pending") return null;
    return merged.find((s) => s.idx === inst.current_step_index) || null;
  }, [merged, inst]);

  const isEligible = currentStep?.run?.eligible_user_ids?.includes(me.id);
  const canCancel = inst?.status === "pending" && (
    inst?.requested_by_user_id === me.id ||
    ["owner", "admin"].includes(me.role)
  );

  const submitDecision = async (decision, reason) => {
    if (!currentStep) return;
    setBusy(true);
    try {
      await api.post(
        `/approval-chains/instances/${id}/steps/${currentStep.step_id}/decisions`,
        { decision, ...(reason ? { reason } : {}) }
      );
      toast.success(decision === "approve"
        ? t("chains.toast_approved")
        : t("chains.toast_rejected"));
      setRejectOpen(false); setRejectReason("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : JSON.stringify(d || "failed"));
    } finally {
      setBusy(false);
    }
  };

  const cancelInstance = async () => {
    setBusy(true);
    try {
      await api.post(`/approval-chains/instances/${id}/cancel`);
      toast.success(t("chains.toast_cancelled"));
      setCancelOpen(false);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : JSON.stringify(d || "failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground p-8" data-testid="chain-detail-loading">
        {t("chains.loading")}
      </div>
    );
  }
  if (!inst) {
    return (
      <div className="text-sm text-muted-foreground p-8" data-testid="chain-detail-not-found">
        {t("chains.not_found")}
      </div>
    );
  }

  return (
    <div data-testid="approval-chain-detail-page" className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        data-testid="chain-detail-back"
      >
        <ChevronLeft className="size-4 mr-1" />
        {t("chains.back")}
      </Button>

      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {defn?.name || defn?.key || t("chains.detail_title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono">{defn?.key}</span>
            {defn?.version ? <> · v{defn.version}</> : null}
            {" · "}
            <span className="font-mono">{inst.subject?.kind}</span>
            {" / "}
            <span className="font-mono">{inst.subject?.id}</span>
          </p>
          <div className="mt-1">
            <AuditHistoryLink kind="approval_chain" id={inst.id}
                              testid="approval-chain-audit-history-link" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={inst.status} />
          {canCancel && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCancelOpen(true)}
              data-testid="chain-cancel-btn"
            >
              {t("chains.cancel")}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <ol className="space-y-6" data-testid="chain-stepper">
            {merged.map((s, idx) => {
              const isCurrent = inst.status === "pending" && idx === inst.current_step_index;
              const runStatus = s.run?.status ||
                (idx < inst.current_step_index ? "approved" : "upcoming");
              return (
                <li
                  key={s.step_id}
                  data-testid={`chain-step-${s.step_id}`}
                  className={[
                    "rounded-md border p-4 transition-colors",
                    isCurrent ? "border-foreground/40 bg-accent/30" : "",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-3">
                    <StepIcon status={runStatus} />
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="font-medium">
                            {idx + 1}. {s.name}
                            {isCurrent && (
                              <Badge
                                variant="secondary"
                                className="ml-2"
                                data-testid="chain-current-badge"
                              >
                                {t("chains.current")}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                            <span className="font-mono">{s.decision_policy}</span>
                            {s.quorum_count
                              ? <span>· quorum={s.quorum_count}</span>
                              : null}
                            <span>
                              · {t("chains.approvers")}:{" "}
                              {(s.approvers?.roles || []).join(", ") || "—"}
                              {(s.approvers?.user_ids || []).length > 0
                                ? ` + ${s.approvers.user_ids.length} ${t("chains.named_users")}`
                                : ""}
                            </span>
                          </div>
                        </div>
                        {s.run && (
                          <Badge
                            variant={STATUS_VARIANTS[runStatus] || "outline"}
                            data-testid={`chain-step-status-${s.step_id}`}
                          >
                            {runStatus}
                          </Badge>
                        )}
                      </div>

                      {s.run?.skip_reason && (
                        <div
                          className="rounded-md border bg-muted/30 p-2 text-xs"
                          data-testid={`chain-step-skip-${s.step_id}`}
                        >
                          <span className="text-muted-foreground uppercase tracking-wide text-[10px] mr-1">
                            {t("chains.skipped_reason")}:
                          </span>
                          {s.run.skip_reason === "condition_false"
                            ? t("chains.skip_condition_false")
                            : s.run.skip_reason}
                          {s.run.condition_snapshot && (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-muted-foreground">
                                {t("chains.snapshot")}
                              </summary>
                              <pre className="mt-1 text-[11px] overflow-auto">
                                {JSON.stringify(s.run.condition_snapshot, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      )}

                      {(s.run?.decisions || []).length > 0 && (
                        <div
                          className="space-y-1"
                          data-testid={`chain-step-decisions-${s.step_id}`}
                        >
                          {s.run.decisions.map((d, di) => (
                            <div
                              key={di}
                              className="text-xs flex items-center gap-2 rounded border bg-muted/30 px-2 py-1"
                              data-testid={`chain-decision-${s.step_id}-${di}`}
                            >
                              <Badge
                                variant={d.decision === "approve" ? "default" : "destructive"}
                              >
                                {d.decision}
                              </Badge>
                              <span className="font-mono">
                                {(d.user_id || "").slice(0, 8)}
                              </span>
                              <span className="text-muted-foreground">
                                · {d.role}
                              </span>
                              <span className="text-muted-foreground ml-auto">
                                {d.decided_at
                                  ? new Date(d.decided_at).toLocaleString()
                                  : ""}
                              </span>
                              {d.reason && (
                                <span className="text-muted-foreground">
                                  — {d.reason}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {isCurrent && isEligible && (
                        <div className="flex items-center gap-2 pt-1">
                          <Button
                            size="sm"
                            onClick={() => submitDecision("approve")}
                            disabled={busy}
                            data-testid="chain-decide-approve"
                          >
                            {t("chains.approve")}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setRejectOpen(true)}
                            disabled={busy}
                            data-testid="chain-decide-reject"
                          >
                            {t("chains.reject")}
                          </Button>
                        </div>
                      )}
                      {isCurrent && !isEligible && (
                        <p
                          className="text-xs text-muted-foreground"
                          data-testid="chain-not-eligible"
                        >
                          {t("chains.not_eligible")}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="chain-reject-dialog">
          <DialogHeader>
            <DialogTitle>{t("chains.reject_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("chains.reject_help")}
          </p>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("chains.reject_placeholder")}
            data-testid="chain-reject-reason"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRejectOpen(false)}
              data-testid="chain-reject-cancel"
            >
              {t("chains.cancel_btn")}
            </Button>
            <Button
              variant="destructive"
              disabled={busy || rejectReason.trim().length < 10}
              onClick={() => submitDecision("reject", rejectReason.trim())}
              data-testid="chain-reject-submit"
            >
              {t("chains.reject_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="chain-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{t("chains.cancel_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("chains.cancel_help")}
          </p>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCancelOpen(false)}
              data-testid="chain-cancel-back"
            >
              {t("chains.cancel_btn")}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={cancelInstance}
              data-testid="chain-cancel-confirm"
            >
              {t("chains.cancel_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(inst?.status === "approved" || inst?.status === "rejected") && (
        <p className="text-xs text-muted-foreground" data-testid="chain-final-note">
          {t("chains.final_note")}{" "}
          <Link
            className="underline"
            to={`/app/approvals?status=${inst.status}`}
          >
            {t("chains.see_legacy")}
          </Link>
        </p>
      )}
    </div>
  );
}
